import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import type { LoaderDataWithOverrideLocale } from '#/horizon/utils/getMatchesOverrideLocale';
import { getI18n } from '#/i18n/getI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { salaryApiServerFetchCompensationSurvey } from '#/modules/salary/api/salaryApiGetCompensationSurvey';
import {
  salariesExplorePath,
  salariesSurveyPath,
} from '#/modules/salary/routing/salary.paths';
import type { RoutePathLoaderArgs } from '#/routing/classes/types/RoutePathLoaderArgs';
import type { RouteProps } from '#/routing/types/RouteProps';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { isBrowserNavigationRequest } from '#/utils/url/isBrowserNavigationRequest';

import { SalarySurveyPage } from './SalarySurvey.page';

export async function loader({
  request,
  url,
  context,
}: RoutePathLoaderArgs<typeof salariesSurveyPath>) {
  const { step } = salariesSurveyPath.searchParamsFromUrl(url);
  if (!isBrowserNavigationRequest(request) && step) {
    return idealistRedirect({
      context,
      fromUrl: url,
      to: salariesSurveyPath.with({}),
    });
  }

  const { serverContext } = getServerRouterContext(context);

  const { submittedTooManyTimes } =
    await salaryApiServerFetchCompensationSurvey({
      request,
      context,
    })();

  if (submittedTooManyTimes) {
    const { i18n } = getI18n(serverContext.locale);

    return idealistRedirect({
      fromUrl: url,
      context,
      to: salariesExplorePath.with({}),
      flash: i18n(
        'We already have your survey information, but we love your enthusiasm!',
      ),
    });
  }

  return {
    handle: {
      variant: 'base',
    } satisfies LayoutHandleProps,
    overrideLocale: I18nLocale.EN,
  } satisfies LoaderDataWithOverrideLocale & Record<string, unknown>;
}

export default function SalarySurveyRoute(_: RouteProps<typeof loader>) {
  return (
    <RouteModule
      title="Idealist"
      ogImage={getStaticImageSrc('compdb/og-image.png')}
    >
      <SalarySurveyPage />
    </RouteModule>
  );
}
